


































































import { computed, defineComponent, onMounted, ref, ssrRef, useAsync, useContext, watch } from '@nuxtjs/composition-api'
import { FAQPage, WithContext } from 'schema-dts'

import MapLocation from '@/classes/MapLocation'

import useDefaultMeta from '@/composables/use-default-meta'
import useI18n from '@/composables/use-i18n'
import useSearchBar from '@/composables/use-search-bar'
import CitySuggestion from '@/classes/CitySuggestion'
import * as storage from '@/modules/location-picker-storage'
import SearchBar from '@/components/search-bar/SearchBar.vue'
import ggoApi from '@/modules/ggo-api'
import Review from '@/classes/Review'
import useBreakpoint from '@/composables/use-breakpoint'

export default defineComponent({
  layout: 'standard',
  name: 'Suggestion',
  head: {},
  watchQuery: ['city'],
  jsonld () {
    const result = []

    if ((this.overviews as CitySuggestion['overviews']).length !== 0) {
      result.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: (this.overviews as CitySuggestion['overviews'])
          ?.map(({ title: name, content: text }) => ({
            '@type': 'Question',
            name,
            suggestedAnswer: { '@type': 'Answer', text },
            acceptedAnswer: { '@type': 'Answer', text },
          })),
      } as WithContext<FAQPage>)
    }

    return result
  },
  // WORKAROUND: 檢查 API 是否可用
  async asyncData ({ error, params }) {
    try {
      if (!params.city) throw new Error('Missing `params.city`!')
      const { success, error } = await ggoApi.getCitySuggestionInfo({
        city: params.city,
      })

      if (!success) throw new Error(error ?? '')
    } catch (e) {
      error({ statusCode: 404 })
    }
  },
  setup () {
    const { query, error, params } = useContext()

    const asyncData = ssrRef<CitySuggestion | null>(null)

    const fetchAsyncData = async () => {
      if (!params.value.city) throw new Error('Missing `params.city`!')

      const { data, success } = await ggoApi.getCitySuggestionInfo({ city: params.value.city })
      if (!success || !data) {
        error({ statusCode: 404 })
      } else {
        asyncData.value = new CitySuggestion(data)
      }
    }
    useAsync(fetchAsyncData)

    const cityName = computed(() => asyncData.value?.name)
    const collections = computed(() => asyncData.value?.collections ?? null)
    const overviews = computed(() => asyncData.value?.overviews ?? [])
    const storeInventories = computed(() => asyncData.value?.storeInventories ?? [])
    const defaultLocation = computed(() => {
      const data = asyncData.value
      if (!data || !data.lat || !data.lng) return null
      return new MapLocation(
        { lat: data.lat, lng: data.lng },
        data.location,
        data.country,
        data.maxDistance,
      )
    })
    const reviews = computed(() => {
      if (!asyncData.value?.comments) return []
      else {
        return asyncData.value.comments.map(comment => new Review({
          name: comment.name,
          content: comment.store_comment,
          score: comment.store_score_all,
          brand: comment.brand,
        }))
      }
    })

    const calOverviewsLength = ref(false)

    watch(overviews, (val) => {
      for (let i = 0; i < val.length; i++) {
        if (val[i].title) {
          calOverviewsLength.value = true
          break
        }
      }
    })

    const i18n = useI18n()
    // HACK: 此程式碼必須放置在 `cityName` 宣告處之後!
    useDefaultMeta({
      title: computed(() => i18n.te(`SEO.city-suggestion.${params.value.city}.title`) ? String(i18n.t(`SEO.city-suggestion.${params.value.city}.title`, [cityName.value])) : String(i18n.t('SEO.city-suggestion.title', [cityName.value]))),
      description: computed(() => String(i18n.t('SEO.city-suggestion.metaDescription', [cityName.value]))),
    })

    /**
     * SearchBar 的租還車地點是透過 local storage 的 location picker 紀錄值
     * 所以 defaultLocation 的計算跟 SearchBar mounting 會有時間差
     * 因此, 以一個是否 defaultLocation 已載入
     * 作為 SearchBar 是否 mount 的依據
     */
    const isDefaultLocationLoaded = ref(false)

    /**
     * 進入 suggestion 頁後, 更改 local storage 的 location picker 紀錄值
     * 租車還車地點皆相同
     */
    watch(defaultLocation, (val) => {
      if (val) {
        storage.set(val)
        storage.setReturnLocation(val)

        setReturnLocationFromStorage()

        isDefaultLocationLoaded.value = true
      }
    })

    const {
      location,
      locationReturn,
      dateHourRange,
      seats,
      queryObject,
      setValueFromQuery,
      saveLocation,
      type,
      pushQuery,
      setReturnLocationFromStorage,
    } = useSearchBar()

    onMounted(() => {
      // 設定地點選擇器的預設值
      watch(
        defaultLocation,
        (value) => { location.value = value },
        { immediate: true }
      )
      watch(query, setValueFromQuery, { immediate: true })
    })

    const search = () => {
      pushQuery(queryObject.value)
      saveLocation()
    }

    const searchBarRef = ref<InstanceType<typeof SearchBar>>()

    const { match } = useBreakpoint()
    onMounted(() => {
      watch(searchBarRef, (ref) => {
        // PC 彈出時間選擇器, 增加跳轉search頁轉換率
        const isMobile = !match('lg')
        if (!isMobile && ref) {
          setTimeout(() => {
            ref.showDatePicker()
          }, 1000)
        }
      }, { immediate: true })
    })

    return {
      cityName,
      suggestionCityPath: params.value.city,
      collections,
      overviews,
      calOverviewsLength,

      storeInventories,

      defaultLocation,
      reviews,

      type,
      location,
      locationReturn,
      dateHourRange,
      seats,
      search,

      isDefaultLocationLoaded,

      searchBarRef,
    }
  },
})
